import {
    MDBContainer,
    MDBCol,
    MDBCard,
    MDBCardBody,
    MDBCardHeader,
    MDBCardTitle,
    MDBTextArea,
    MDBBtn
} from "mdb-react-ui-kit";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  checkIsAdmin,
    convertISOToReadableDate,
    getNotificationRules,
    getNotificationRulesVersions,
    showErrorToast,
    showSuccessToast,
    updateNotificationRules
} from "../../../helpers";
import { useLoader } from "stm-frontend-library";

const JSONBlock = styled.pre`
    white-space: pre-wrap;
    word-break: break-word;
    padding: 1rem;
    border-radius: 0.5rem;
    font-size: 0.9rem;
`;

const VersionInfo = styled.div`
    margin-top: 0.5rem;
    color: #6c757d;
    font-size: 0.875rem;

    div {
        margin-bottom: 0.25rem;
    }

    strong {
        font-weight: 600;
    }
`;

const NotificationRules = () => {
    const isAdmin = checkIsAdmin();
    const [notificationRules, setNotificationRules] = useState(null);
    const [lastNotificationRulesVersion, setLastNotificationRulesVersion] = useState(null);
    const [editedRules, setEditedRules] = useState('');
    const [isJsonValid, setIsJsonValid] = useState(true);

    const textAreaRef = useRef(null);
    const { hideLoader, showLoader } = useLoader();

    const fetchData = async () => {
        try {
            showLoader();
            const [rulesRes, versionsRes] = await Promise.all([
                getNotificationRules(),
                getNotificationRulesVersions()
            ]);

            const rules = rulesRes.data?.rules;
            setNotificationRules(rules);
            setEditedRules(JSON.stringify(rules, null, 2));
            setLastNotificationRulesVersion(versionsRes.data?.versions?.[0]);
        } catch (error) {
            showErrorToast();
        } finally {
            hideLoader();
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (textAreaRef.current) {
            textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
        }
    }, [editedRules]);

    const handleChange = (e) => {
        const value = e.target.value;
        setEditedRules(value);
        try {
            JSON.parse(value);
            setIsJsonValid(true);
        } catch (err) {
            setIsJsonValid(false);
        }
    };

    const handleSubmit = async () => {
        try {
            showLoader();
            const parsed = JSON.parse(editedRules);
            const body = { rules: parsed }
            await updateNotificationRules(body);
            showSuccessToast();
            await fetchData();
        } catch (err) {
            showErrorToast();
        }
        finally {
            hideLoader();
        }
    };

    const renderVersionInfo = () => {
        if (!lastNotificationRulesVersion) return null;
        const { timestamp, createdBy } = lastNotificationRulesVersion;

        return (
            <VersionInfo>
                {timestamp && (
                    <div>
                        <strong>Last version:</strong> {convertISOToReadableDate(Date(timestamp))}
                    </div>
                )}
                {createdBy && (
                    <div>
                        <strong>Created by:</strong> {createdBy}
                    </div>
                )}
            </VersionInfo>
        );
    };

    return (
        <MDBContainer className="my-5">
            <MDBCol>
                {notificationRules && (
                    <MDBCard className="my-3 shadow-sm">
                        <MDBCardHeader>
                            <MDBCardTitle className="mb-0">Notification Rules</MDBCardTitle>
                            {renderVersionInfo()}
                        </MDBCardHeader>
                        <MDBCardBody>
                            <MDBTextArea
                                disabled={!isAdmin}
                                readOnly={!isAdmin}
                                label="Rules JSON"
                                id="textArea"
                                value={editedRules}
                                onChange={handleChange}
                                rows={4}
                                inputRef={textAreaRef}
                                className={isJsonValid ? "" : "is-invalid"}
                            />
                            {!isJsonValid && (
                                <div className="text-danger mt-1">
                                    Invalid JSON format
                                </div>
                            )}
                            <MDBBtn
                                className="mt-3"
                                onClick={handleSubmit}
                                disabled={!isJsonValid || !isAdmin}
                            >
                                Update Rules
                            </MDBBtn>
                        </MDBCardBody>
                    </MDBCard>
                )}
            </MDBCol>
        </MDBContainer>
    );
};

export default NotificationRules;
